import { memo, useCallback, useState, useMemo } from 'react';
import classNames from 'classnames';
import { MdClose, MdChevronLeft } from 'react-icons/md';
import {
  V2QsMultiTextNestedProps,
  V2QsMultiTextNestedTag,
  defaultV2QsMultiTextNestedProps,
} from './types';

/**
 * V2QsMultiTextNested 컴포넌트
 *
 * 중첩 옵션이 있는 다중 텍스트 입력 컴포넌트
 * V1 QsInputMultiTextNested의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 태그 형태의 다중 텍스트 입력
 * - 각 태그에 대한 중첩 옵션 선택 모달
 * - 옵션에 대한 노트 입력 지원
 */
function V2QsMultiTextNested(props: V2QsMultiTextNestedProps) {
  const {
    value = [],
    onChange,
    nestedQuestions = [],
    openTagIndex,
    onOpenModal,
    onCloseModal,
    min,
    max,
    isDisplayMode = defaultV2QsMultiTextNestedProps.isDisplayMode,
    disabled = defaultV2QsMultiTextNestedProps.disabled,
    readOnly = defaultV2QsMultiTextNestedProps.readOnly,
    hasError = defaultV2QsMultiTextNestedProps.hasError,
    errorMessage,
    placeholder = defaultV2QsMultiTextNestedProps.placeholder,
    addButtonText = defaultV2QsMultiTextNestedProps.addButtonText,
    selectButtonText = defaultV2QsMultiTextNestedProps.selectButtonText,
    closeButtonText = defaultV2QsMultiTextNestedProps.closeButtonText,
    minErrorTemplate = defaultV2QsMultiTextNestedProps.minErrorTemplate,
    maxErrorTemplate = defaultV2QsMultiTextNestedProps.maxErrorTemplate,
    className,
  } = props;

  const [inputValue, setInputValue] = useState('');

  const replaceTemplateValue = useCallback((template: string | undefined, key: 'min' | 'max', replacement: number) => {
    if (!template) return template;
    const tokenValue = String(replacement);
    return template
      .replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), tokenValue)
      .replace(new RegExp(`\\{${key}\\}`, 'g'), tokenValue);
  }, []);

  // 유효성 검사
  const validationError = useMemo(() => {
    if (min != null && value.length < min) {
      return replaceTemplateValue(minErrorTemplate, 'min', min);
    }
    if (max != null && value.length > max) {
      return replaceTemplateValue(maxErrorTemplate, 'max', max);
    }
    return null;
  }, [value.length, min, max, minErrorTemplate, maxErrorTemplate, replaceTemplateValue]);

  const currentTag = openTagIndex !== null && openTagIndex !== undefined
    ? value[openTagIndex]
    : null;

  // 태그 추가
  const handleAddTag = useCallback(() => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || disabled || readOnly || isDisplayMode) return;

    // 중복 체크
    if (value.some((tag) => tag.text === trimmedValue)) return;

    const newTag: V2QsMultiTextNestedTag = {
      id: `tag-${Date.now()}`,
      text: trimmedValue,
      selectedOptions: [],
      notesForKeys: {},
    };

    onChange?.([...value, newTag]);
    setInputValue('');
  }, [inputValue, value, onChange, disabled, readOnly, isDisplayMode]);

  // 태그 제거
  const handleRemoveTag = useCallback(
    (indexToRemove: number) => {
      if (disabled || readOnly || isDisplayMode) return;
      const newValue = value.filter((_, idx) => idx !== indexToRemove);
      onChange?.(newValue);
    },
    [value, onChange, disabled, readOnly, isDisplayMode]
  );

  // 옵션 토글
  const handleOptionToggle = useCallback(
    (optionKey: string, checked: boolean) => {
      if (openTagIndex === null || openTagIndex === undefined) return;
      if (disabled || readOnly || isDisplayMode) return;

      const newValue = [...value];
      const tag = { ...newValue[openTagIndex] };
      let selectedOptions = [...tag.selectedOptions];

      // mutuallyExclusive 옵션 찾기
      const allOptions = nestedQuestions.flatMap((q) => q.options);
      const option = allOptions.find((opt) => opt.key === optionKey);
      const isMutuallyExclusive = option?.mutuallyExclusive;

      if (checked) {
        if (isMutuallyExclusive) {
          // 상호 배타적 옵션은 단독 선택
          selectedOptions = [optionKey];
        } else {
          // 상호 배타적 옵션 제거 후 추가
          const mutExcKeys = allOptions
            .filter((opt) => opt.mutuallyExclusive)
            .map((opt) => opt.key);
          selectedOptions = selectedOptions.filter(
            (k) => !mutExcKeys.includes(k)
          );
          selectedOptions.push(optionKey);
        }
      } else {
        selectedOptions = selectedOptions.filter((k) => k !== optionKey);
      }

      tag.selectedOptions = selectedOptions;
      newValue[openTagIndex] = tag;
      onChange?.(newValue);
    },
    [value, openTagIndex, onChange, nestedQuestions, disabled, readOnly, isDisplayMode]
  );

  // 노트 업데이트
  const handleNoteChange = useCallback(
    (optionKey: string, noteValue: string) => {
      if (openTagIndex === null || openTagIndex === undefined) return;
      if (disabled || readOnly || isDisplayMode) return;

      const newValue = [...value];
      const tag = { ...newValue[openTagIndex] };
      tag.notesForKeys = {
        ...tag.notesForKeys,
        [optionKey]: noteValue,
      };
      newValue[openTagIndex] = tag;
      onChange?.(newValue);
    },
    [value, openTagIndex, onChange, disabled, readOnly, isDisplayMode]
  );

  // 선택된 옵션에서 옵션 제거
  const handleRemoveOption = useCallback(
    (tagIndex: number, optionKey: string) => {
      if (disabled || readOnly || isDisplayMode) return;

      const newValue = [...value];
      const tag = { ...newValue[tagIndex] };
      tag.selectedOptions = tag.selectedOptions.filter((k) => k !== optionKey);
      newValue[tagIndex] = tag;
      onChange?.(newValue);
    },
    [value, onChange, disabled, readOnly, isDisplayMode]
  );

  // 옵션 라벨 찾기
  const getOptionLabel = useCallback(
    (optionKey: string) => {
      const allOptions = nestedQuestions.flatMap((q) => q.options);
      const found = allOptions.find((o) => o.key === optionKey);
      return found?.label || optionKey;
    },
    [nestedQuestions]
  );

  const isEditable = !disabled && !readOnly && !isDisplayMode;

  return (
    <div className={classNames('V2QsMultiTextNested', className)}>
      {/* 유효성 에러 */}
      {validationError && (
        <div className="text-error opacity-70 p-1 mb-2">{validationError}</div>
      )}

      {/* 입력 필드 */}
      <div className="relative">
        <input
          type="text"
          className={classNames(
            'input input-bordered w-full rounded-xl pr-20',
            (hasError || validationError) && 'input-error'
          )}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled || readOnly || isDisplayMode}
        />
        {/* ADD 버튼 */}
        <button
          type="button"
          onClick={handleAddTag}
          disabled={!inputValue.trim() || !isEditable}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 btn btn-sm"
          style={{
            height: '32px',
            minHeight: '32px',
            borderRadius: '8px',
            backgroundColor: inputValue.trim() && isEditable ? '#3555FC' : '#E5E7EB',
            borderColor: inputValue.trim() && isEditable ? '#3555FC' : '#E5E7EB',
            cursor: inputValue.trim() && isEditable ? 'pointer' : 'not-allowed',
            paddingLeft: '12px',
            paddingRight: '12px',
          }}
        >
          <span
            style={{
              color: inputValue.trim() && isEditable ? 'white' : '#9CA3AF',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            {addButtonText}
          </span>
        </button>
      </div>

      {/* 태그 목록 */}
      {value.length > 0 && (
        <div className="mt-3 space-y-3">
          {value.map((tag, index) => (
            <div
              key={tag.id}
              className="border rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors relative"
              style={{ borderColor: '#3555FC', backgroundColor: 'transparent' }}
              onClick={() => isEditable && onOpenModal?.(index)}
            >
              {/* 태그 헤더 */}
              <div className="flex items-center justify-between mb-3">
                <span
                  className="font-semibold text-lg"
                  style={{ color: '#3555FC' }}
                >
                  {tag.text}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTag(index);
                    }}
                    className="btn btn-ghost btn-xs btn-circle bg-white hover:bg-gray-100"
                  >
                    <MdClose size={16} />
                  </button>
                )}
              </div>

              {/* 중첩 질문 표시 */}
              <div className="flex gap-3">
                <div
                  className="w-1 bg-gray-300 rounded-full"
                  style={{
                    height: tag.selectedOptions.length > 0 ? '140px' : '80px',
                  }}
                />
                <div className="flex-1">
                  {/* 질문 텍스트 및 SELECT */}
                  <div className="mb-3">
                    <div className="text-gray-700 mb-3">
                      {nestedQuestions.length > 0
                        ? nestedQuestions[0].question
                        : placeholder}
                    </div>
                    <div className="relative">
                      <div className="w-full p-3 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 transition-colors">
                        <span className="text-gray-500">{selectButtonText}</span>
                        <MdChevronLeft
                          size={16}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 -rotate-90 text-gray-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 선택된 옵션 태그 */}
                  {tag.selectedOptions.length > 0 && (
                    <div className="bg-white rounded-lg">
                      <div className="flex flex-wrap gap-2">
                        {tag.selectedOptions.map((optionKey) => (
                          <div
                            key={optionKey}
                            className="inline-flex items-center gap-1 pl-3 pr-1 py-2 rounded-lg"
                            style={{
                              height: '40px',
                              minHeight: '40px',
                              fontSize: '14px',
                              fontWeight: '500',
                              backgroundColor: '#E0E5FF',
                              color: '#3455FC',
                            }}
                          >
                            <span>{getOptionLabel(optionKey)}</span>
                            {isEditable && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveOption(index, optionKey);
                                }}
                                className="btn btn-ghost btn-xs btn-circle hover:bg-blue-200"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  minWidth: '16px',
                                  minHeight: '16px',
                                }}
                              >
                                <MdClose size={10} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 중첩 옵션 선택 모달 */}
      {openTagIndex !== null && openTagIndex !== undefined && currentTag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-lg rounded-t-3xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* 모달 헤더 */}
            <div className="flex-none p-4 border-b">
              <div className="flex justify-center mb-2">
                <div className="bg-gray-300 rounded-full w-10 h-1" />
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {currentTag.text} - {addButtonText}
                </h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={onCloseModal}
                >
                  <MdClose size={20} />
                </button>
              </div>
            </div>

            {/* 모달 컨텐츠 */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {nestedQuestions.map((nestedQuestion, questionIndex) => (
                <div
                  key={questionIndex}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="font-medium text-gray-700 mb-2">
                    {nestedQuestion.question}
                  </div>
                  <div className="space-y-2">
                    {nestedQuestion.options.map((option) => {
                      const isSelected = currentTag.selectedOptions.includes(
                        option.key
                      );
                      const requireNoteInput =
                        isSelected && option.requireNote;

                      return (
                        <div key={option.key}>
                          <label className="cursor-pointer border border-gray-300 p-3 rounded-lg border-opacity-20 flex flex-col gap-3 hover:border-gray-400 transition-colors">
                            <div className="flex-1 w-full flex items-center gap-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                className="checkbox checkbox-lg checkbox-primary"
                                onChange={(e) =>
                                  handleOptionToggle(
                                    option.key,
                                    e.target.checked
                                  )
                                }
                                disabled={!isEditable}
                              />
                              <span>{option.label}</span>
                            </div>

                            {/* 노트 입력 */}
                            {requireNoteInput && (
                              <input
                                type="text"
                                className="input input-bordered w-full rounded-xl"
                                placeholder={placeholder}
                                value={currentTag.notesForKeys?.[option.key] || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  handleNoteChange(option.key, e.target.value)
                                }
                                disabled={!isEditable}
                              />
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* 모달 푸터 */}
            <div className="flex-none p-4 border-t">
              <button
                type="button"
                className="btn w-full"
                onClick={onCloseModal}
              >
                {closeButtonText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 에러 메시지 */}
      {hasError && errorMessage && (
        <p className="text-error text-sm mt-2">{errorMessage}</p>
      )}
    </div>
  );
}

export default memo(V2QsMultiTextNested);
